import { useState, useEffect, useRef } from 'react'
import BottomNav from '../components/BottomNav'
import {BET_CONTENT_MAP, type GameRuleDTO} from '../types/betting'
import type { BetContent, PlaceBetCmd, PlaceFastThreeBetCmd } from '../types/betting'
import {placeBet, getCurrentPeriod, getUserBalance, getLatestDrawResult, getGameRules} from '../services/api'

export default function FastThree() {

  // 遊戲狀態
  const [periodId, setPeriodId] = useState<number | null>(null)
  const [currentPeriod, setCurrentPeriod] = useState('——')
  const [countdown, setCountdown] = useState(-1)
  const [balance, setBalance] = useState<number>(-1)
  const [lastResult, setLastResult] = useState<number[]>([])

  // 投注狀態
  const [selectedBets, setSelectedBets] = useState<PlaceFastThreeBetCmd[]>([])
  const [betAmount, setBetAmount] = useState(10)

  // 投注類型標籤
  const [activeTab, setActiveTab] = useState('sum')

  // 多選狀態
  const [selectedThreeDifferent, setSelectedThreeDifferent] = useState<number[]>([])
  const [selectedTwoDifferent, setSelectedTwoDifferent] = useState<number[]>([])
  const [selectedSingleDice, setSelectedSingleDice] = useState<number[]>([])
  const [selectedDoubleMultiple, setSelectedDoubleMultiple] = useState<number[]>([])

  // 防重复调用的 ref
  const hasInitialized = useRef(false)
  const isInitializing = useRef(false)

  // 添加规则状态
  const [rulesMap, setRulesMap] = useState<Map<string, GameRuleDTO>>(new Map())

  const [periodEndTime, setPeriodEndTime] = useState<number | null>(null)

  // 初始化數據
  useEffect(() => {
    loadInitialData()
  }, [])

  const calculateCountdown = (endTime: number): number => {
    const now = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
    const remaining = endTime - now
    return Math.max(0, remaining) // 确保不会返回负数
  }

  const loadInitialData = async () => {
    if (isInitializing.current || hasInitialized.current) {
      return
    }

    isInitializing.current = true

    try {
      // 獲取用戶餘額
      const userBalance = await getUserBalance()
      setBalance(userBalance)
      // 获取当前期数
      const period = await getCurrentPeriod()
      if (!period) {
        return
      }
      setPeriodId(period.periodId)
      setCurrentPeriod(period.periodNo)
      if (period.canBet) {
        // 获取游戏规则
        const rules = await getGameRules('fast3')
        // 创建规则映射表，方便快速查找
        const ruleMap = new Map<string, GameRuleDTO>()
        rules.forEach(rule => {
          if (rule.status === 1) { // 只加载启用的规则
            ruleMap.set(rule.ruleCode, rule)
          }
        })
        setRulesMap(ruleMap)
      }
      if (period.endTime) {
        setPeriodEndTime(period.endTime)
        const initialCountdown = calculateCountdown(period.endTime)
        setCountdown(initialCountdown)
      }

      // 获取最新开奖结果
      try {
        const latestDrawResult = await getLatestDrawResult('fast3')
        setLastResult([latestDrawResult.dice1, latestDrawResult.dice2, latestDrawResult.dice3])
      } catch (resultError) {
        console.warn('获取开奖结果失败，保持当前结果:', resultError)

      }

      hasInitialized.current = true

    } catch (error) {
      console.error('初始化數據失敗:', error)
    } finally {
      isInitializing.current = false
    }
  }

  // 获取规则信息
  const getRuleInfo = (ruleCode: string) => {
    const rule = rulesMap.get(ruleCode)
    return {
      odds: rule?.odds || 0,
      minBetAmount: rule?.minBetAmount || 1,
      maxBetAmount: rule?.maxBetAmount || 1000,
      isEnabled: rule?.status === 1
    }
  }

  // 获取赔率显示文本
  const getOddsDisplay = (ruleCode: string): string => {
    const ruleInfo = getRuleInfo(ruleCode)
    if (!ruleInfo.isEnabled) {
      return '暂停'
    }
    return `${ruleInfo.odds}倍`
  }

  // 验证投注金额
  const validateBetAmount = (ruleCode: string, amount: number): { isValid: boolean, message?: string } => {
    const rule = rulesMap.get(ruleCode)

    if (!rule || rule.status !== 1) {
      return { isValid: false, message: '该投注类型暂不可用222' }
    }

    if (amount < rule.minBetAmount) {
      return { isValid: false, message: `最小投注金额为 ${rule.minBetAmount} 元` }
    }

    if (amount > rule.maxBetAmount) {
      return { isValid: false, message: `最大投注金额为 ${rule.maxBetAmount} 元` }
    }

    return { isValid: true }
  }

  // 处理投注选择时添加验证
  const handleBetSelection = (betType: string, betContent: string, displayName: string) => {
    // 验证规则是否可用
    let usingBetCode = betContent;
    if (betType === 'three_different' || betType == 'two_different') {
      usingBetCode = betType;
    }
    const ruleInfo = getRuleInfo(usingBetCode)
    if (!ruleInfo.isEnabled) {
      showToast('该投注类型暂不可用')
      return
    }

    // 验证投注金额
    const validation = validateBetAmount(usingBetCode, betAmount)
    if (!validation.isValid) {
      showToast(validation.message || '投注验证失败')
      return
    }

    const newBet: PlaceFastThreeBetCmd = {
      betType,
      betContent,
      betAmount
    }
    setSelectedBets([...selectedBets, newBet])
    showToast(`已添加投注：${displayName}`)
  }

  // 4. 修改倒计时的 useEffect
  useEffect(() => {
    const timer = setInterval(() => {
      if (periodEndTime) {
        // 基于真实的endTime计算倒计时
        const remaining = calculateCountdown(periodEndTime)
        setCountdown(remaining)

        // 当倒计时结束时，重新加载数据获取新期数
        if (remaining <= 0) {
          setSelectedBets([])
          // 重置初始化状态，允许重新加载数据
          hasInitialized.current = false
          loadInitialData() // 重新加載新期數
        }
      }
      // } else {
      //   // 如果没有endTime，使用原来的逻辑
      //   setCountdown(prev => {
      //     if (prev <= 1) {
      //       setSelectedBets([])
      //       hasInitialized.current = false
      //       loadInitialData()
      //       return 120
      //     }
      //     return prev - 1
      //   })
      // }
    }, 1000)

    return () => clearInterval(timer)
  }, [periodEndTime])

  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // 處理和值投注
  const handleSumBet = (value: number) => {
    const betType = 'sum'
    const betContent = `sum_${value}`
    const displayName = `和值${value}`
    handleBetSelection(betType, betContent, displayName)
  }

  // 處理三同號單選
  const handleTripleSingle = (num: number) => {
    const betType = 'triple_single'
    const betContent = `triple_single_${num}${num}${num}`
    const displayName = `三同號單選${num}${num}${num}`
    handleBetSelection(betType, betContent, displayName)
  }

  // 處理二同號單選
  const handleDoubleSingle = (num: number) => {
    const betType = 'double_single'
    const betContent = `double_single_${num}${num}`
    const displayName = `二同號單選${num}${num}`
    handleBetSelection(betType, betContent, displayName)
  }

  // 處理二同號復選
  const handleDoubleMultiple = (num: number) => {
    if (selectedDoubleMultiple.includes(num)) {
      setSelectedDoubleMultiple(selectedDoubleMultiple.filter(n => n !== num))
    } else {
      setSelectedDoubleMultiple([...selectedDoubleMultiple, num])
    }
  }

  // 確認二同號復選
  const confirmDoubleMultiple = () => {
    // 批量添加所有選中的投注
    const newBets: PlaceFastThreeBetCmd[] = []

    for (const num of selectedDoubleMultiple) {
      const betContent = `double_multiple_${num}${num}`
      const ruleInfo = getRuleInfo(betContent)

      // 验证规则是否可用
      if (!ruleInfo.isEnabled) {
        showToast(`二同號復選${num}${num} 暂不可用`)
        continue
      }

      // 验证投注金额
      const validation = validateBetAmount(betContent, betAmount)
      if (!validation.isValid) {
        showToast(`二同號復選${num}${num}: ${validation.message}`)
        continue
      }

      newBets.push({
        betType: 'double_multiple',
        betContent,
        betAmount
      })
    }

    if (newBets.length > 0) {
      setSelectedBets(prevBets => [...prevBets, ...newBets])

      // 顯示提示
      const displayNames = newBets.map((_, index) => {
        const num = selectedDoubleMultiple[index]
        return `二同號復選${num}${num}`
      }).join('、')
      showToast(`已添加投注：${displayNames}`)
    }

    setSelectedDoubleMultiple([])
  }

  // 處理三不同號選擇
  const handleThreeDifferentSelect = (num: number) => {
    if (selectedThreeDifferent.includes(num)) {
      setSelectedThreeDifferent(selectedThreeDifferent.filter(n => n !== num))
    } else if (selectedThreeDifferent.length < 3) {
      setSelectedThreeDifferent([...selectedThreeDifferent, num])
    }
  }

  // 處理二不同號選擇
  const handleTwoDifferentSelect = (num: number) => {
    if (selectedTwoDifferent.includes(num)) {
      setSelectedTwoDifferent(selectedTwoDifferent.filter(n => n !== num))
    } else if (selectedTwoDifferent.length < 2) {
      setSelectedTwoDifferent([...selectedTwoDifferent, num])
    }
  }

  // 處理單骰選擇
  const handleSingleDiceSelect = (num: number) => {
    if (selectedSingleDice.includes(num)) {
      setSelectedSingleDice(selectedSingleDice.filter(n => n !== num))
    } else {
      setSelectedSingleDice([...selectedSingleDice, num])
    }
  }

  // 確認多選投注
  const confirmMultiSelect = (type: string) => {
    switch(type) {
      case 'threeDifferent':
        if (selectedThreeDifferent.length === 3) {
          const betType = 'three_different'
          const betContent = selectedThreeDifferent.join('')
          const displayName = `三不同號 ${selectedThreeDifferent.join(',')}`
          handleBetSelection(betType, betContent, displayName)
          setSelectedThreeDifferent([])
        }
        break
      case 'twoDifferent':
        if (selectedTwoDifferent.length === 2) {
          const betType = 'two_different'
          const betContent = selectedTwoDifferent.join('')
          const displayName = `二不同號 ${selectedTwoDifferent.join(',')}`
          handleBetSelection(betType, betContent, displayName)
          setSelectedTwoDifferent([])
        }
        break
      case 'singleDice':
        // 批量添加所有選中的單骰投注
        { const newSingleDiceBets: PlaceFastThreeBetCmd[] = []

        for (const num of selectedSingleDice) {
          const betContent = `single_dice_${num}`
          const ruleInfo = getRuleInfo(betContent)

          // 验证规则是否可用
          if (!ruleInfo.isEnabled) {
            showToast(`單骰${num} 暂不可用`)
            continue
          }

          // 验证投注金额
          const validation = validateBetAmount(betContent, betAmount)
          if (!validation.isValid) {
            showToast(`單骰${num}: ${validation.message}`)
            continue
          }

          newSingleDiceBets.push({
            betType: 'single_dice',
            betContent,
            betAmount
          })
        }

        if (newSingleDiceBets.length > 0) {
          setSelectedBets(prevBets => [...prevBets, ...newSingleDiceBets])

          // 顯示提示
          const singleDiceNames = newSingleDiceBets.map((_, index) => {
            const num = selectedSingleDice[index]
            return `單骰${num}`
          }).join('、')
          showToast(`已添加投注：${singleDiceNames}`)
        }

        setSelectedSingleDice([])
        break }
    }
  }

  // 確認投注
  const confirmBet = async () => {
    if (selectedBets.length === 0 || !periodId) return

    const totalAmount = selectedBets.reduce((sum, bet) => sum + bet.betAmount, 0)
    if (totalAmount > balance) {
      showToast('餘額不足！')
      return
    }

    try {
      const betData: PlaceBetCmd = {
        gameType: 'fast3',
        periodId: periodId,
        fast3Bet: selectedBets
      }
      const result = await placeBet(betData)
      console.log("result",result)
      if (result) {
        showToast(`投注成功`)
        await refreshAfterBet()
      } else {
        showToast(result.message || '投注失敗')
      }
    } catch (error) {
      console.error('投注失敗:', error)
      showToast('投注失敗，請稍後再試')
    }
  }

  const refreshAfterBet = async () => {
    const userBalance = await getUserBalance()
    setBalance(userBalance)
    setSelectedBets([])
  }

  // Toast 提示
  const showToast = (message: string) => {
    const toast = document.createElement('div')
    toast.textContent = message
    toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 24px;
      border-radius: 25px;
      z-index: 10000;
      font-weight: bold;
    `
    document.body.appendChild(toast)
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast)
      }
    }, 2000)
  }

  // 獲取投注顯示名稱
  const getBetDisplayName = (bet: PlaceFastThreeBetCmd) => {
    return BET_CONTENT_MAP[bet.betContent as BetContent] || bet.betContent
  }

  return (
      <div style={{
        fontFamily: 'Microsoft YaHei, sans-serif',
        background: 'var(--gradient-purple)',
        minHeight: '100vh',
        padding: '10px',
        paddingBottom: '100px',
        margin: 0
      }}>
        <div style={{
          maxWidth: '480px',
          margin: '0 auto',
          background: 'var(--bg-card)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-heavy)',
          overflow: 'hidden'
        }}>
          {/* 遊戲頭部 */}
          <div className="game-header-enhanced">
            <h1 style={{ fontSize: '1.8em', margin: '10px 0', fontWeight: 900 }}>
              🎲 快三下注遊戲
            </h1>

            <div style={{ marginTop: '15px' }}>
              {/* 第一行：期數和倒計時 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                marginBottom: '10px'
              }}>
                <div className="info-card-enhanced">
                  <div style={{ fontSize: '0.9em', marginBottom: '3px' }}>當前期數</div>
                  <div style={{ fontSize: '1em', fontWeight: 'bold' }}>{currentPeriod}</div>
                </div>
                <div className="info-card-enhanced">
                  <div style={{fontSize: '0.9em', marginBottom: '3px'}}>倒計時</div>
                  <div
                      style={{
                        fontSize: '1.5em',
                        fontWeight: '900',
                        color: countdown <= 60 ? 'var(--primary-red)' : 'var(--primary-gold)', // 最后1分钟显示红色
                      }}
                  >
                    {countdown < 0 ? '——' : formatCountdown(countdown)}
                  </div>
                </div>
              </div>

              {/* 第二行：餘額 */}
              <div className="info-card-enhanced" style={{width: '100%'}}>
                <div style={{fontSize: '0.9em', marginBottom: '3px' }}>餘額</div>
                <div style={{ fontSize: '1.3em', fontWeight: 'bold', color: 'var(--primary-gold)' }}>
                  {balance < 0 ? '-' : `¥${balance.toFixed(2)}`}
                </div>
              </div>
            </div>
          </div>

          {/* 上期開獎結果 */}
          <div style={{ padding: '15px', background: 'white' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
              🌟 上期開獎結果
            </h3>
            {lastResult.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                    {lastResult.map((num, index) => (
                        <div key={index} style={{
                          width: '50px',
                          height: '50px',
                          background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8em',
                          fontWeight: 'bold',
                          color: 'var(--primary-red)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                          border: '2px solid #e0e0e0'
                        }}>
                          {num}
                        </div>
                    ))}
                  </div>
                  <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.95em' }}>
                    和值：<span style={{ color: 'var(--primary-red)', fontWeight: 'bold' }}>
                  {lastResult.reduce((sum, num) => sum + num, 0)}
                </span>
                  </div>
                </>
            ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
                  暂无开奖结果
                </div>
            )}
          </div>

          {/* 投注類型標籤 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            padding: '10px',
            borderBottom: '1px solid #e9ecef',
            background: 'white',
            overflowX: 'auto'
          }}>
            {['sum', 'triple', 'different', 'double', 'singleDice', 'bigSmall', 'oddEven'].map((tab) => (
                <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '8px 10px',
                      fontSize: '0.8em',
                      border: 'none',
                      background: 'transparent',
                      color: activeTab === tab ? 'var(--primary-red)' : 'var(--text-secondary)',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      borderBottom: activeTab === tab ? '2px solid var(--primary-red)' : '2px solid transparent',
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap'
                    }}
                >
                  {tab === 'sum' && '和值'}
                  {tab === 'triple' && '三同號'}
                  {tab === 'different' && '不同號'}
                  {tab === 'double' && '二同號'}
                  {tab === 'singleDice' && '單骰'}
                  {tab === 'bigSmall' && '大小'}
                  {tab === 'oddEven' && '單雙'}
                </button>
            ))}
          </div>

          {/* 投注區域 */}
          <div style={{ padding: '15px', background: 'white' }}>
            {/* 和值投注 */}
            {activeTab === 'sum' && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 和值投注
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '8px'
                  }}>
                    {Array.from({length: 16}, (_, i) => i + 3).map((value) => {
                      const ruleCode = `sum_${value}`
                      const ruleInfo = getRuleInfo(ruleCode)
                      return (
                          <button
                              key={value}
                              onClick={() => handleSumBet(value)}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                    : '#f0f0f0',
                                border: '2px solid #e9ecef',
                                borderRadius: '10px',
                                padding: '10px 5px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                textAlign: 'center' as const,
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '3px' }}>
                              {value}
                            </div>
                            <div style={{
                              fontSize: '0.7em',
                              color: 'var(--lucky-orange)',
                              background: 'rgba(255, 215, 0, 0.2)',
                              padding: '2px 5px',
                              borderRadius: '10px'
                            }}>
                              {getOddsDisplay(ruleCode)}
                            </div>
                          </button>
                      )
                    })}
                  </div>
                </div>
            )}

            {/* 三同號投注 */}
            {activeTab === 'triple' && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 三同號投注
                  </h3>

                  {/* 三同號通選 */}
                  {(() => {
                    const ruleInfo = getRuleInfo('triple_all')
                    return (
                        <button
                            onClick={() => handleBetSelection('triple_all', 'triple_all', '三同號通選')}
                            disabled={!ruleInfo.isEnabled}
                            style={{
                              width: '100%',
                              background: ruleInfo.isEnabled
                                  ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                  : '#f0f0f0',
                              border: '2px solid #e9ecef',
                              borderRadius: '10px',
                              padding: '15px',
                              cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                              marginBottom: '15px',
                              textAlign: 'center' as const,
                              opacity: ruleInfo.isEnabled ? 1 : 0.5
                            }}
                        >
                          <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '5px' }}>
                            三同號通選
                          </div>
                          <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                            任意三同號
                          </div>
                          <div style={{
                            fontSize: '0.85em',
                            color: 'var(--lucky-orange)',
                            background: 'rgba(255, 215, 0, 0.2)',
                            padding: '3px 8px',
                            borderRadius: '10px',
                            display: 'inline-block'
                          }}>
                            {getOddsDisplay('triple_all')}
                          </div>
                        </button>
                    )
                  })()}

                  {/* 三連號通選 */}
                  {(() => {
                    const ruleInfo = getRuleInfo('three_consecutive')
                    return (
                        <button
                            onClick={() => handleBetSelection('three_consecutive', 'three_consecutive', '三連號通選')}
                            disabled={!ruleInfo.isEnabled}
                            style={{
                              width: '100%',
                              background: ruleInfo.isEnabled
                                  ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                  : '#f0f0f0',
                              border: '2px solid #e9ecef',
                              borderRadius: '10px',
                              padding: '15px',
                              cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                              marginBottom: '15px',
                              textAlign: 'center' as const,
                              opacity: ruleInfo.isEnabled ? 1 : 0.5
                            }}
                        >
                          <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '5px' }}>
                            三連號通選
                          </div>
                          <div style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                            123, 234, 345, 456
                          </div>
                          <div style={{
                            fontSize: '0.85em',
                            color: 'var(--lucky-orange)',
                            background: 'rgba(255, 215, 0, 0.2)',
                            padding: '3px 8px',
                            borderRadius: '10px',
                            display: 'inline-block'
                          }}>
                            {getOddsDisplay('three_consecutive')}
                          </div>
                        </button>
                    )
                  })()}

                  {/* 三同號單選 */}
                  <h4 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '1em' }}>三同號單選</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {[1, 2, 3, 4, 5, 6].map(num => {
                      const ruleCode = `triple_single_${num}${num}${num}`
                      const ruleInfo = getRuleInfo(ruleCode)
                      return (
                          <button
                              key={num}
                              onClick={() => handleTripleSingle(num)}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                    : '#f0f0f0',
                                border: '2px solid #e9ecef',
                                borderRadius: '10px',
                                padding: '10px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                textAlign: 'center' as const,
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '3px' }}>
                              {num}{num}{num}
                            </div>
                            <div style={{
                              fontSize: '0.7em',
                              color: 'var(--lucky-orange)',
                              background: 'rgba(255, 215, 0, 0.2)',
                              padding: '2px 5px',
                              borderRadius: '10px'
                            }}>
                              {getOddsDisplay(ruleCode)}
                            </div>
                          </button>
                      )
                    })}
                  </div>
                </div>
            )}

            {/* 不同號投注 */}
            {activeTab === 'different' && (
                <div>
                  {/* 三不同號 */}
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 三不同號 (選3個)
                  </h3>
                  <div style={{
                    background: 'rgba(116, 75, 162, 0.1)',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '2px solid rgba(116, 75, 162, 0.3)',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: '8px'
                    }}>
                      {[1, 2, 3, 4, 5, 6].map(num => (
                          <button
                              key={num}
                              onClick={() => handleThreeDifferentSelect(num)}
                              style={{
                                aspectRatio: '1',
                                minWidth: '40px',
                                border: selectedThreeDifferent.includes(num) ?
                                    '2px solid var(--success-green)' : '2px solid #dee2e6',
                                borderRadius: '8px',
                                background: selectedThreeDifferent.includes(num) ?
                                    'var(--success-green)' : 'white',
                                color: selectedThreeDifferent.includes(num) ? 'white' : 'var(--text-primary)',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                          >
                            {num}
                          </button>
                      ))}
                    </div>
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: 'white',
                      borderRadius: '8px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.95em',
                      fontWeight: 'bold'
                    }}>
                      已選: {selectedThreeDifferent.join(', ') || '請選擇3個不同的數字'}
                    </div>
                    {(() => {
                      const ruleInfo = getRuleInfo('three_different')
                      return (
                          <button
                              onClick={() => confirmMultiSelect('threeDifferent')}
                              disabled={selectedThreeDifferent.length !== 3 || !ruleInfo.isEnabled}
                              style={{
                                width: '100%',
                                marginTop: '10px',
                                padding: '10px',
                                background: (selectedThreeDifferent.length === 3 && ruleInfo.isEnabled) ?
                                    'var(--gradient-wealth)' : '#ccc',
                                border: 'none',
                                borderRadius: '10px',
                                color: (selectedThreeDifferent.length === 3 && ruleInfo.isEnabled) ? 'var(--text-primary)' : 'white',
                                fontWeight: 'bold',
                                cursor: (selectedThreeDifferent.length === 3 && ruleInfo.isEnabled) ? 'pointer' : 'not-allowed'
                              }}
                          >
                            確認投注 (賠率 {getOddsDisplay('three_different')})
                          </button>
                      )
                    })()}
                  </div>

                  {/* 二不同號 */}
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 二不同號 (選2個)
                  </h3>
                  <div style={{
                    background: 'rgba(116, 75, 162, 0.1)',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '2px solid rgba(116, 75, 162, 0.3)'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)',
                      gap: '8px' // 从 '10px' 改为 '8px'
                    }}>
                      {[1, 2, 3, 4, 5, 6].map(num => (
                          <button
                              key={num}
                              onClick={() => handleTwoDifferentSelect(num)}
                              style={{
                                aspectRatio: '1',
                                minWidth: '40px',
                                border: selectedTwoDifferent.includes(num) ?
                                    '2px solid var(--success-green)' : '2px solid #dee2e6',
                                borderRadius: '8px',
                                background: selectedTwoDifferent.includes(num) ?
                                    'var(--success-green)' : 'white',
                                color: selectedTwoDifferent.includes(num) ? 'white' : 'var(--text-primary)',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                          >
                            {num}
                          </button>
                      ))}
                    </div>
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: 'white',
                      borderRadius: '8px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.95em',
                      fontWeight: 'bold'
                    }}>
                      已選: {selectedTwoDifferent.join(', ') || '請選擇2個不同的數字'}
                    </div>
                    {(() => {
                      const ruleInfo = getRuleInfo('two_different')
                      return (
                          <button
                              onClick={() => confirmMultiSelect('twoDifferent')}
                              disabled={selectedTwoDifferent.length !== 2 || !ruleInfo.isEnabled}
                              style={{
                                width: '100%',
                                marginTop: '10px',
                                padding: '10px',
                                background: (selectedTwoDifferent.length === 2 && ruleInfo.isEnabled) ?
                                    'var(--gradient-wealth)' : '#ccc',
                                border: 'none',
                                borderRadius: '10px',
                                color: (selectedTwoDifferent.length === 2 && ruleInfo.isEnabled) ? 'var(--text-primary)' : 'white',
                                fontWeight: 'bold',
                                cursor: (selectedTwoDifferent.length === 2 && ruleInfo.isEnabled) ? 'pointer' : 'not-allowed'
                              }}
                          >
                            確認投注 (賠率 {getOddsDisplay('two_different')})
                          </button>
                      )
                    })()}
                  </div>
                </div>
            )}

            {/* 二同號投注 */}
            {activeTab === 'double' && (
                <div>
                  {/* 二同號單選 */}
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 二同號單選
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
                    {[1, 2, 3, 4, 5, 6].map(num => {
                      const ruleCode = `double_single_${num}${num}`
                      const ruleInfo = getRuleInfo(ruleCode)
                      return (
                          <button
                              key={num}
                              onClick={() => handleDoubleSingle(num)}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                    : '#f0f0f0',
                                border: '2px solid #e9ecef',
                                borderRadius: '10px',
                                padding: '10px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                textAlign: 'center' as const,
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '3px' }}>
                              {num}{num}
                            </div>
                            <div style={{
                              fontSize: '0.7em',
                              color: 'var(--lucky-orange)',
                              background: 'rgba(255, 215, 0, 0.2)',
                              padding: '2px 5px',
                              borderRadius: '10px'
                            }}>
                              {getOddsDisplay(ruleCode)}
                            </div>
                          </button>
                      )
                    })}
                  </div>

                  {/* 二同號復選 */}
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 二同號復選 (可多選)
                  </h3>
                  <div style={{
                    background: 'rgba(116, 75, 162, 0.1)',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '2px solid rgba(116, 75, 162, 0.3)'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                      {[1, 2, 3, 4, 5, 6].map(num => {
                        const ruleCode = `double_multiple_${num}${num}`
                        const ruleInfo = getRuleInfo(ruleCode)
                        return (
                            <button
                                key={num}
                                onClick={() => handleDoubleMultiple(num)}
                                disabled={!ruleInfo.isEnabled}
                                style={{
                                  background: selectedDoubleMultiple.includes(num) ?
                                      'var(--success-green)' : (ruleInfo.isEnabled
                                          ? 'linear-gradient(145deg, #ffffff, #f8f9fa)'
                                          : '#f0f0f0'),
                                  border: selectedDoubleMultiple.includes(num) ?
                                      '2px solid var(--success-green)' : '2px solid #e9ecef',
                                  borderRadius: '10px',
                                  padding: '10px',
                                  cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                  color: selectedDoubleMultiple.includes(num) ? 'white' : 'var(--text-primary)',
                                  textAlign: 'center' as const,
                                  opacity: ruleInfo.isEnabled ? 1 : 0.5
                                }}
                            >
                              <div style={{ fontSize: '1.1em', fontWeight: 'bold', marginBottom: '3px' }}>
                                {num}{num}
                              </div>
                              <div style={{
                                fontSize: '0.7em',
                                color: selectedDoubleMultiple.includes(num) ? 'white' : 'var(--lucky-orange)',
                                background: selectedDoubleMultiple.includes(num) ?
                                    'rgba(255, 255, 255, 0.2)' : 'rgba(255, 215, 0, 0.2)',
                                padding: '2px 5px',
                                borderRadius: '10px'
                              }}>
                                {getOddsDisplay(ruleCode)}
                              </div>
                            </button>
                        )
                      })}
                    </div>
                    <button
                        onClick={confirmDoubleMultiple}
                        disabled={selectedDoubleMultiple.length === 0}
                        style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px',
                          background: selectedDoubleMultiple.length > 0 ?
                              'var(--gradient-wealth)' : '#ccc',
                          border: 'none',
                          borderRadius: '10px',
                          color: selectedDoubleMultiple.length > 0 ? 'var(--text-primary)' : 'white',
                          fontWeight: 'bold',
                          cursor: selectedDoubleMultiple.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                    >
                      確認投注 ({selectedDoubleMultiple.length}個)
                    </button>
                  </div>
                </div>
            )}

            {/* 單骰投注 */}
            {activeTab === 'singleDice' && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 單骰投注 (可多選)
                  </h3>
                  <div style={{
                    background: 'rgba(116, 75, 162, 0.1)',
                    padding: '15px',
                    borderRadius: '10px',
                    border: '2px solid rgba(116, 75, 162, 0.3)'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 1fr)', // 保持6列等宽
                      gap: '8px', // 减小间距
                      maxWidth: '100%', // 确保不超出容器
                      margin: '0 auto' // 居中显示
                    }}>
                      {[1, 2, 3, 4, 5, 6].map(num => {
                        const ruleCode = `single_dice_${num}`
                        const ruleInfo = getRuleInfo(ruleCode)
                        return (
                            <button
                                key={num}
                                onClick={() => handleSingleDiceSelect(num)}
                                disabled={!ruleInfo.isEnabled}
                                style={{
                                  aspectRatio: '1', // 保持正方形比例
                                  minWidth: '40px', // 最小宽度
                                  border: selectedSingleDice.includes(num) ?
                                      '2px solid var(--success-green)' : '2px solid #dee2e6',
                                  borderRadius: '8px',
                                  background: selectedSingleDice.includes(num) ?
                                      'var(--success-green)' : (ruleInfo.isEnabled ? 'white' : '#f0f0f0'),
                                  color: selectedSingleDice.includes(num) ? 'white' : 'var(--text-primary)',
                                  cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                  fontWeight: 'bold',
                                  fontSize: '1.1em',
                                  opacity: ruleInfo.isEnabled ? 1 : 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                            >
                              {num}
                            </button>
                        )
                      })}
                    </div>
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: 'white',
                      borderRadius: '8px',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.95em',
                      fontWeight: 'bold'
                    }}>
                      已選: {selectedSingleDice.sort().join(', ') || '請選擇要投注的數字'}
                    </div>
                    <button
                        onClick={() => confirmMultiSelect('singleDice')}
                        disabled={selectedSingleDice.length === 0}
                        style={{
                          width: '100%',
                          marginTop: '10px',
                          padding: '10px',
                          background: selectedSingleDice.length > 0 ?
                              'var(--gradient-wealth)' : '#ccc',
                          border: 'none',
                          borderRadius: '10px',
                          color: selectedSingleDice.length > 0 ? 'var(--text-primary)' : 'white',
                          fontWeight: 'bold',
                          cursor: selectedSingleDice.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                    >
                      確認投注 (賠率視出現次數)
                    </button>
                  </div>
                </div>
            )}

            {/* 大小投注 */}
            {activeTab === 'bigSmall' && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 大小投注
                  </h3>

                  <div style={{ marginBottom: '15px', fontSize: '0.9em', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    大：和值11-17 | 小：和值4-10
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    {(() => {
                      const ruleInfo = getRuleInfo('big')
                      return (
                          <button
                              onClick={() => handleBetSelection('big_small', 'big', '大')}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                                    : '#f0f0f0',
                                border: 'none',
                                borderRadius: '15px',
                                padding: '30px 20px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                textAlign: 'center' as const,
                                color: ruleInfo.isEnabled ? 'white' : '#999',
                                boxShadow: ruleInfo.isEnabled ? 'var(--shadow-colored)' : 'none',
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '3em', marginBottom: '10px' }}>📈</div>
                            <div style={{ fontSize: '2em', fontWeight: 'bold', marginBottom: '5px' }}>大</div>
                            <div style={{ fontSize: '0.9em', opacity: 0.9, marginBottom: '8px' }}>和值 11-17</div>
                            <div style={{
                              fontSize: '1em',
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              display: 'inline-block'
                            }}>
                              賠率 {getOddsDisplay('big')}
                            </div>
                          </button>
                      )
                    })()}

                    {(() => {
                      const ruleInfo = getRuleInfo('small')
                      return (
                          <button
                              onClick={() => handleBetSelection('big_small', 'small', '小')}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                                    : '#f0f0f0',
                                border: 'none',
                                borderRadius: '15px',
                                padding: '30px 20px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                textAlign: 'center' as const,
                                color: ruleInfo.isEnabled ? 'white' : '#999',
                                boxShadow: ruleInfo.isEnabled ? '0 8px 25px rgba(116, 185, 255, 0.3)' : 'none',
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '3em', marginBottom: '10px' }}>📉</div>
                            <div style={{ fontSize: '2em', fontWeight: 'bold', marginBottom: '5px' }}>小</div>
                            <div style={{ fontSize: '0.9em', opacity: 0.9, marginBottom: '8px' }}>和值 4-10</div>
                            <div style={{
                              fontSize: '1em',
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              display: 'inline-block'
                            }}>
                              賠率 {getOddsDisplay('small')}
                            </div>
                          </button>
                      )
                    })()}
                  </div>

                  {/* 顯示上期開獎結果的大小 */}
                  {lastResult.length > 0 && (
                      <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        background: 'rgba(0, 184, 148, 0.1)',
                        borderRadius: '10px',
                        border: '2px solid rgba(0, 184, 148, 0.3)',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                          上期開獎結果
                        </div>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                          和值 {lastResult.reduce((sum, num) => sum + num, 0)} =
                          <span style={{
                            color: lastResult.reduce((sum, num) => sum + num, 0) >= 11 && lastResult.reduce((sum, num) => sum + num, 0) <= 17 ? 'var(--primary-red)' : 'var(--royal-blue)',
                            marginLeft: '5px',
                            fontSize: '1.3em'
                          }}>
                      {lastResult.reduce((sum, num) => sum + num, 0) >= 11 && lastResult.reduce((sum, num) => sum + num, 0) <= 17 ? '大' : '小'}
                    </span>
                        </div>
                      </div>
                  )}
                </div>
            )}

            {/* 單雙投注 */}
            {activeTab === 'oddEven' && (
                <div>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.1em', color: 'var(--text-primary)' }}>
                    💎 單雙投注
                  </h3>

                  <div style={{ marginBottom: '15px', fontSize: '0.9em', color: 'var(--text-secondary)', textAlign: 'center' }}>
                    單：和值為奇數 | 雙：和值為偶數
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    {(() => {
                      const ruleInfo = getRuleInfo('odd')
                      return (
                          <button
                              onClick={() => handleBetSelection('odd_even', 'odd', '單')}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)'
                                    : '#f0f0f0',
                                border: 'none',
                                borderRadius: '15px',
                                padding: '30px 20px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                textAlign: 'center' as const,
                                color: ruleInfo.isEnabled ? 'white' : '#999',
                                boxShadow: ruleInfo.isEnabled ? '0 8px 25px rgba(253, 121, 168, 0.3)' : 'none',
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '3em', marginBottom: '10px' }}>🔥</div>
                            <div style={{ fontSize: '2em', fontWeight: 'bold', marginBottom: '5px' }}>單</div>
                            <div style={{ fontSize: '0.9em', opacity: 0.9, marginBottom: '8px' }}>和值為奇數</div>
                            <div style={{
                              fontSize: '1em',
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              display: 'inline-block'
                            }}>
                              賠率 {getOddsDisplay('odd')}
                            </div>
                          </button>
                      )
                    })()}

                    {(() => {
                      const ruleInfo = getRuleInfo('even')
                      return (
                          <button
                              onClick={() => handleBetSelection('odd_even', 'even', '雙')}
                              disabled={!ruleInfo.isEnabled}
                              style={{
                                background: ruleInfo.isEnabled
                                    ? 'linear-gradient(135deg, #00b894 0%, #00a085 100%)'
                                    : '#f0f0f0',
                                border: 'none',
                                borderRadius: '15px',
                                padding: '30px 20px',
                                cursor: ruleInfo.isEnabled ? 'pointer' : 'not-allowed',
                                transition: 'all 0.3s ease',
                                textAlign: 'center' as const,
                                color: ruleInfo.isEnabled ? 'white' : '#999',
                                boxShadow: ruleInfo.isEnabled ? '0 8px 25px rgba(0, 184, 148, 0.3)' : 'none',
                                opacity: ruleInfo.isEnabled ? 1 : 0.5
                              }}
                          >
                            <div style={{ fontSize: '3em', marginBottom: '10px' }}>❄️</div>
                            <div style={{ fontSize: '2em', fontWeight: 'bold', marginBottom: '5px' }}>雙</div>
                            <div style={{ fontSize: '0.9em', opacity: 0.9, marginBottom: '8px' }}>和值為偶數</div>
                            <div style={{
                              fontSize: '1em',
                              background: 'rgba(255, 255, 255, 0.2)',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              display: 'inline-block'
                            }}>
                              賠率 {getOddsDisplay('even')}
                            </div>
                          </button>
                      )
                    })()}
                  </div>

                  {/* 顯示上期開獎結果的單雙 */}
                  {lastResult.length > 0 && (
                      <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        background: 'rgba(253, 121, 168, 0.1)',
                        borderRadius: '10px',
                        border: '2px solid rgba(253, 121, 168, 0.3)',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                          上期開獎結果
                        </div>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                          和值 {lastResult.reduce((sum, num) => sum + num, 0)} =
                          <span style={{
                            color: lastResult.reduce((sum, num) => sum + num, 0) % 2 === 1 ? '#fd79a8' : '#00b894',
                            marginLeft: '5px',
                            fontSize: '1.3em'
                          }}>
                      {lastResult.reduce((sum, num) => sum + num, 0) % 2 === 1 ? '單' : '雙'}
                    </span>
                        </div>
                      </div>
                  )}
                </div>
            )}

            {/* 投注金額控制 */}
            <div style={{
              background: 'var(--gradient-purple)',
              borderRadius: '15px',
              padding: '20px',
              marginTop: '20px',
              color: 'white'
            }}>
              <h3 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '1.1em' }}>
                💰 投注金額
              </h3>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}>
                <button
                    onClick={() => setBetAmount(Math.max(1, betAmount - 1))}
                    style={{
                      width: '35px',
                      height: '35px',
                      borderRadius: '50%',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1em',
                      cursor: 'pointer'
                    }}
                >
                  -
                </button>
                <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => setBetAmount(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{
                      width: '100px',
                      padding: '8px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderRadius: '8px',
                      background: 'rgba(255, 255, 255, 0.9)',
                      textAlign: 'center',
                      fontSize: '1.2em',
                      fontWeight: 'bold',
                      color: 'var(--text-primary)'
                    }}
                />
                <button
                    onClick={() => setBetAmount(betAmount + 1)}
                    style={{
                      width: '35px',
                      height: '35px',
                      borderRadius: '50%',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1em',
                      cursor: 'pointer'
                    }}
                >
                  +
                </button>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                marginTop: '15px'
              }}>
                {[5, 10, 20, 50, 100, 200].map(amount => (
                    <button
                        key={amount}
                        onClick={() => setBetAmount(amount)}
                        style={{
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.2)',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '15px',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.9em',
                          cursor: 'pointer'
                        }}
                    >
                      {amount}元
                    </button>
                ))}
              </div>
            </div>

            {/* 投注摘要 */}
            {selectedBets.length > 0 && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '10px',
                  padding: '15px',
                  marginTop: '15px',
                  border: '2px solid rgba(255, 215, 0, 0.3)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ margin: 0, fontSize: '1em', color: 'var(--text-primary)' }}>
                      投注摘要
                    </h4>
                    <button
                        onClick={() => setSelectedBets([])}
                        style={{
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                          border: 'none',
                          borderRadius: '15px',
                          padding: '5px 15px',
                          color: 'white',
                          fontSize: '0.8em',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                    >
                      🗑️ 清除
                    </button>
                  </div>
                  {selectedBets.map((bet, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '5px',
                        fontSize: '0.9em'
                      }}>
                        <span>{getBetDisplayName(bet)}</span>
                        <span>{bet.betAmount}元</span>
                      </div>
                  ))}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '2px solid var(--primary-gold)',
                    paddingTop: '8px',
                    fontWeight: 'bold',
                    fontSize: '1em',
                    color: 'var(--success-green)'
                  }}>
                    <strong>總金額</strong>
                    <strong>{selectedBets.reduce((sum, bet) => sum + bet.betAmount, 0)}元</strong>
                  </div>
                </div>
            )}

            {/* 確認投注 */}
            <button
                onClick={confirmBet}
                disabled={selectedBets.length === 0}
                style={{
                  width: '100%',
                  marginTop: '15px',
                  padding: '15px',
                  background: selectedBets.length > 0 ? 'var(--gradient-wealth)' : '#ccc',
                  border: 'none',
                  borderRadius: '25px',
                  fontSize: '1.1em',
                  fontWeight: '900',
                  color: selectedBets.length > 0 ? 'var(--text-primary)' : 'white',
                  cursor: selectedBets.length > 0 ? 'pointer' : 'not-allowed',
                  boxShadow: selectedBets.length > 0 ? 'var(--shadow-wealth)' : 'none'
                }}
            >
              💰 確認投注 ({selectedBets.reduce((sum, bet) => sum + bet.betAmount, 0)}元) 🎯
            </button>
          </div>
        </div>

        {/* 底部導航 */}
        <BottomNav />
      </div>
  )
}